import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  styled,
  Divider,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useScreenSize from "hooks/ScreenSize";
import { MobileWidth } from "entities/constants";
import { TextSizeContext } from "data/index";
import { PersonalTabPropType } from "entities/interfaces";
import WithPersonalTabData from "ui/containers/settings/PersonalTabContainer";
import ImageComponent from "ui/components/shared/ImageComponent";
import TextBox from "ui/components/shared/TextBox";
import { SubmitButton } from "ui/components/shared/SubmitButton";
import ImageUploadModal from "../modals/ImageUploadModal";

import PersonImage from "assets/images/person.jpg";
import CameraIcon from "assets/icons/camera.png";
import { POST } from "api/index";

// === Styled Components ===
const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  padding: "2rem",
  gap: "2.5rem",
  [theme.breakpoints.down("sm")]: {
    padding: "1.5rem 1rem",
  },
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: "2rem",
  borderRadius: "20px",
  boxShadow: theme.shadows[1],
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
}));

const Header = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.5rem",
  color: theme.palette.text.primary,
}));

const ProfileSection = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "2rem",
}));

const ProfileImage = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "6.5rem",
  height: "6.5rem",
  borderRadius: "50%",
  overflow: "hidden",
  boxShadow: theme.shadows[1],
  cursor: "pointer",
}));

const CameraOverlay = styled(Box)(() => ({
  position: "absolute",
  bottom: 0,
  height: "40%",
  width: "100%",
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const InfoText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.secondary,
}));

// === Component ===
const PersonalTab = ({
  userData,
  updateUserData,
  uploadImage,
}: PersonalTabPropType) => {
  const theme = useTheme();
  const { width } = useScreenSize();
  const { t } = useTranslation();
  const { state: textSize } = useContext(TextSizeContext);

  const [formData, setFormData] = useState({
    image: "",
    firstName: "",
    lastName: "",
    email: "",
  });

  const [error, setError] = useState({ firstName: "", email: "" });
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [imageModal, setImageModal] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

useEffect(() => {
  if (userData) {
    setFormData({
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      image: userData.image || PersonImage, // Direkt aus userData
    });
  }
}, [userData]);
  

  const handleFormChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof typeof formData
  ) => {
    setFormData({ ...formData, [key]: e.target.value });
    setError({ ...error, [key]: "" });
    setSaveDisabled(false);
    setGlobalError(null);
  };

  const handleImageCropped = (croppedUrl: string) => {
    setFormData((prev) => ({ ...prev, image: croppedUrl }));
    setSaveDisabled(false);
    localStorage.setItem("currentUserImage", croppedUrl);
  };

  const handleSave = async () => {
    const { firstName, email } = formData;
    if (!firstName || !email) {
      setError({
        firstName: firstName ? "" : `${t("First Name")} ${t("required")}`,
        email: email ? "" : `${t("E-Mail")} ${t("required")}`,
      });
      return;
    }

    try {
      const body = new URLSearchParams();
      body.append("email", email);

      const res = await POST("checkEmailExists", body);
      if (res.result?.exists) {
        setGlobalError(t("This email is already taken"));
        setFormData((prev) => ({ ...prev, email: userData.email }));
        setSaveDisabled(true);
        return;
      }

      const update = await updateUserData(formData);
      if (update.status === 200) {
        setSaveDisabled(true);
        setGlobalError(null);
      }
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
    }
  };

  return (
    <Container>
      <Card>
        <Header>{t("Personal Data")}</Header>

        <ProfileSection>
          <ProfileImage onClick={() => setImageModal(true)}>
            <ImageComponent
              src={formData.image || PersonImage}
              alt="Profilbild"
              width="100%"
              height="100%"
              borderRadius="50%"
            />
            <CameraOverlay>
              <ImageComponent
                src={CameraIcon}
                alt="upload"
                width="1.5rem"
                height="1.5rem"
              />
            </CameraOverlay>
          </ProfileImage>

          <Box>
            <Typography fontWeight={600}>
              {t("Profile picture")}
            </Typography>
            <InfoText>
              {t("A Profile picture helps personalize your account")}
            </InfoText>
          </Box>
        </ProfileSection>

        <Divider />

        <TextBox
          label={t("First Name")}
          value={formData.firstName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleFormChange(e, "firstName")
          }
          error={error.firstName}
          isRequired
        />
        <TextBox
          label={t("Last Name")}
          value={formData.lastName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleFormChange(e, "lastName")
          }
        />
        <TextBox
          label={t("E-Mail")}
          value={formData.email}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleFormChange(e, "email")
          }
          error={error.email}
          isRequired
        />

        {globalError && (
          <Typography color="error" fontWeight={600}>
            {globalError}
          </Typography>
        )}

        <SubmitButton
          color="primary"
          onClick={handleSave}
          disabled={saveDisabled}
        >
          {t("Save")}
        </SubmitButton>
      </Card>

      {imageModal && (
        <ImageUploadModal
          closeModal={() => setImageModal(false)}
          imageUrl={formData.image}
          setImageUrl={handleImageCropped}
          uploadImage={uploadImage}
        />
      )}
    </Container>
  );
};

export default WithPersonalTabData(PersonalTab);
